import { requestState } from './request-state';
import type { ApiErrorResponse } from '@buflow/contracts';

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: string,
    public readonly details?: Record<string, string[]>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

export async function apiRequest<T>(url: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {}, signal } = options;

  requestState.pending++;
  try {
    const response = await fetch(url, {
      method,
      credentials: 'include',
      headers: {
        // 无 body 时不得携带 Content-Type: application/json ——
        // Fastify 对「声明 JSON 却为空」的请求体返回 400
        // (FST_ERR_CTP_EMPTY_JSON_BODY)，如 confirm / logout 等
        // 无 body 的 POST。
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...headers,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    });

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      const error = data as ApiErrorResponse | null;
      throw new ApiError(
        error?.error?.message ?? '请求失败',
        response.status,
        error?.error?.code ?? 'UNKNOWN',
        error?.error?.details,
      );
    }

    if (data === null)
      throw new ApiError('服务器响应无效，请重试', response.status, 'INVALID_RESPONSE');
    return data as T;
  } catch (error) {
    if (typeof error === 'object' && error !== null && 'name' in error && error.name === 'AbortError') throw error;
    const failure =
      error instanceof ApiError
        ? error
        : new ApiError('网络连接失败，请检查网络后重试', 0, 'NETWORK_ERROR');
    requestState.error = failure.message;
    throw error;
  } finally {
    requestState.pending--;
  }
}
